import React, { useState, useEffect, useRef, DragEvent } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Stack,
  Typography,
  Tabs,
  Tab,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  InputAdornment,
  Paper,
  ButtonBase,
  Chip,
} from '@mui/material';
import {
  X,
  Briefcase,
  FileText,
  UploadCloud,
  Search,
  FileUp,
} from 'lucide-react';
import { getJobOpeningList } from '../../../../utils/api_service/JobOpenings';

interface NewSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateFromFile: (file: File) => Promise<void>;
  onCreateFromText: (text: string) => Promise<void>;
  onCreateFromOpening: (jobOpeningId: string) => Promise<void>;
  loading: boolean;
}

type TabType = 'opening' | 'paste' | 'upload';

export const NewSearchModal: React.FC<NewSearchModalProps> = ({
  isOpen,
  onClose,
  onCreateFromFile,
  onCreateFromText,
  onCreateFromOpening,
  loading,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('opening');
  
  // Tab 1: Opening
  const [openings, setOpenings] = useState<any[]>([]);
  const [openingsLoading, setOpeningsLoading] = useState(false);
  const [openingSearchQuery, setOpeningSearchQuery] = useState('');
  const [selectedOpeningId, setSelectedOpeningId] = useState<string | null>(null);

  // Tab 2: Paste
  const [jdText, setJdText] = useState('');

  // Tab 3: Upload
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen && activeTab === 'opening' && openings.length === 0) {
      fetchOpenings();
    }
  }, [isOpen, activeTab]);

  const fetchOpenings = async () => {
    setOpeningsLoading(true);
    try {
      const res = await getJobOpeningList({} as any, 1, 50);
      if (res && res.json) {
        const data = await res.json();
        const items = data.data || data.openings || data.jobOpenings || data;
        setOpenings(Array.isArray(items) ? items : []);
      }
    } catch (error) {
      console.error('Failed to fetch job openings', error);
    } finally {
      setOpeningsLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      if (activeTab === 'opening' && selectedOpeningId) {
        await onCreateFromOpening(selectedOpeningId);
      } else if (activeTab === 'paste' && jdText.trim()) {
        await onCreateFromText(jdText);
      } else if (activeTab === 'upload' && selectedFile) {
        await onCreateFromFile(selectedFile);
      }
    } catch (err) {
      console.error('Failed to create search', err);
    }
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      if (file.name.endsWith('.pdf') || file.name.endsWith('.docx')) {
        setSelectedFile(file);
      } else {
        alert('Please upload a .pdf or .docx file');
      }
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const filteredOpenings = openings.filter((op: any) =>
    (op.title || op.name || op.jobTitle || '').toLowerCase().includes(openingSearchQuery.toLowerCase())
  );

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          bgcolor: 'grey.900',
          color: 'white',
          px: 3,
          py: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'white', lineHeight: 1.2 }}>
            Create New Search
          </Typography>
          <Typography variant="caption" sx={{ color: 'grey.400' }}>
            Extract requirements and search your candidate pool
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: 'grey.400', '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}
        >
          <X size={18} />
        </IconButton>
      </DialogTitle>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.50', px: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_, val) => setActiveTab(val)}
          sx={{ minHeight: 44 }}
        >
          <Tab
            value="opening"
            label="From Opening"
            icon={<Briefcase size={15} />}
            iconPosition="start"
            sx={{ minHeight: 44, fontSize: '0.75rem', fontWeight: 700, textTransform: 'none' }}
          />
          <Tab
            value="paste"
            label="Paste JD"
            icon={<FileText size={15} />}
            iconPosition="start"
            sx={{ minHeight: 44, fontSize: '0.75rem', fontWeight: 700, textTransform: 'none' }}
          />
          <Tab
            value="upload"
            label="Upload JD"
            icon={<UploadCloud size={15} />}
            iconPosition="start"
            sx={{ minHeight: 44, fontSize: '0.75rem', fontWeight: 700, textTransform: 'none' }}
          />
        </Tabs>
      </Box>

      {/* Content */}
      <DialogContent sx={{ p: 3, minHeight: 320 }}>
        {activeTab === 'opening' && (
          <Stack spacing={2} sx={{ height: '100%' }}>
            <TextField
              size="small"
              placeholder="Search openings (including closed)..."
              value={openingSearchQuery}
              onChange={(e) => setOpeningSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={16} />
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem' } }}
            />
            
            <Paper
              elevation={0}
              sx={{
                border: '1px solid',
                borderColor: 'grey.200',
                borderRadius: 2,
                bgcolor: 'grey.50',
                maxHeight: 260,
                overflowY: 'auto',
                flex: 1,
              }}
            >
              {openingsLoading ? (
                <Stack alignItems="center" justifyContent="center" sx={{ height: 180 }}>
                  <CircularProgress size={24} />
                </Stack>
              ) : filteredOpenings.length === 0 ? (
                <Stack alignItems="center" justifyContent="center" sx={{ height: 180 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    No openings found.
                  </Typography>
                </Stack>
              ) : (
                <Stack>
                  {filteredOpenings.map((op: any) => {
                    const id = op._id || op.id;
                    const isSelected = selectedOpeningId === id;
                    return (
                      <ButtonBase
                        key={id}
                        onClick={() => setSelectedOpeningId(id)}
                        sx={{
                          p: 1.5,
                          textAlign: 'left',
                          width: '100%',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          borderBottom: '1px solid',
                          borderColor: 'grey.100',
                          bgcolor: isSelected ? 'primary.50' : 'transparent',
                          borderLeft: isSelected ? '4px solid' : 'none',
                          borderLeftColor: 'primary.main',
                          '&:hover': {
                            bgcolor: isSelected ? 'primary.50' : 'grey.100',
                          },
                        }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 700,
                              color: isSelected ? 'primary.dark' : 'text.primary',
                            }}
                          >
                            {op.title || op.name || op.jobTitle || 'Untitled Opening'}
                          </Typography>
                          {op.jobReferenceNumber && (
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'monospace', fontSize: '0.625rem' }}>
                              #{op.jobReferenceNumber}
                            </Typography>
                          )}
                        </Box>
                        {op.status && (
                          <Chip
                            label={op.status}
                            size="small"
                            sx={{ height: 20, fontSize: '0.625rem', fontWeight: 600 }}
                          />
                        )}
                      </ButtonBase>
                    );
                  })}
                </Stack>
              )}
            </Paper>
          </Stack>
        )}

        {activeTab === 'paste' && (
          <Box sx={{ height: '100%' }}>
            <TextField
              multiline
              rows={9}
              fullWidth
              value={jdText}
              onChange={(e) => setJdText(e.target.value)}
              placeholder="Paste the job description text here..."
              sx={{
                '& .MuiInputBase-root': {
                  fontSize: '0.8125rem',
                  lineHeight: 1.6,
                },
              }}
            />
          </Box>
        )}

        {activeTab === 'upload' && (
          <Stack spacing={2} alignItems="center" justifyContent="center">
            <Box
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              sx={{
                width: '100%',
                p: 4,
                border: '2px dashed',
                borderColor: isDragging ? 'primary.main' : 'grey.300',
                bgcolor: isDragging ? 'primary.50' : 'grey.50',
                borderRadius: 3,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: 'primary.light',
                  bgcolor: 'grey.100',
                },
              }}
            >
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept=".pdf,.docx"
                style={{ display: 'none' }}
              />
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  bgcolor: 'primary.50',
                  color: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 1.5,
                }}
              >
                <FileUp size={24} />
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
                Click or drag JD file here
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Supports PDF and DOCX
              </Typography>
            </Box>
            
            {selectedFile && (
              <Paper
                elevation={0}
                sx={{
                  width: '100%',
                  p: 1.5,
                  bgcolor: 'primary.50',
                  border: '1px solid',
                  borderColor: 'primary.light',
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                <FileText size={18} color="#4f46e5" />
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.dark', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {selectedFile.name}
                </Typography>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedFile(null);
                  }}
                  sx={{ color: 'primary.main' }}
                >
                  <X size={14} />
                </IconButton>
              </Paper>
            )}
          </Stack>
        )}
      </DialogContent>

      {/* Footer */}
      <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'grey.100', bgcolor: 'grey.50' }}>
        <Button
          onClick={onClose}
          color="inherit"
          disabled={loading}
          sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem' }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={
            loading ||
            (activeTab === 'opening' && !selectedOpeningId) ||
            (activeTab === 'paste' && !jdText.trim()) ||
            (activeTab === 'upload' && !selectedFile)
          }
          startIcon={loading ? <CircularProgress size={14} color="inherit" /> : undefined}
          sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.8125rem' }}
        >
          {loading ? 'Processing...' : 'Create Search'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
